import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent as ReactDragEvent,
} from 'react';
import {
  Badge,
  Body1,
  Body1Strong,
  Button,
  Caption1,
  Image,
  ProgressBar,
  Spinner,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { AttachmentPreviewDialog } from '../components/AttachmentPreviewDialog';
import { useResponsiveStyles } from '../hooks/useResponsiveStyles';
import { useRestoreFocusOnClose } from '../hooks/useRestoreFocusOnClose';
import {
  deleteAttachment,
  fetchAttachmentBlob,
  fetchAttachmentLimits,
  uploadAttachment,
  type Attachment,
  type AttachmentLimits,
} from '../api/attachments';
import {
  ALLOWED_ATTACHMENT_TYPES,
  MAX_ATTACHMENTS_PER_REQUEST,
  MAX_ATTACHMENT_SIZE_BYTES,
} from '../lib/attachmentLimits';
import { truncateFileNameMiddle } from '../lib/fileNameDisplay';
import { Section } from '../components/Section';
import { RADIUS, SPACE, SPACE_X } from '../styles/scale';
import { useLayoutStyles, useSurfaceStyles } from '../styles/surfaces';

/**
 * SOW §4.4, wireframe 03.1 (R4/R4a), 09.1 (G3) and 10.1 (D3) — dropzone with a
 * browse button, immediate upload-on-drop with per-file cancellable progress,
 * individually-rejected files that stay visible with their reason, images
 * previewed as thumbnails with a next/previous lightbox, documents as a type
 * chip with a Preview action, and an undo toast for removal (#38): the file
 * disappears from the list immediately, but the actual delete — of the
 * database row and the blob — is deferred until the 10-second window lapses
 * (or a later removal supersedes it), so undo genuinely restores the file.
 *
 * ── Design system pass (#202) ──
 * Nothing this section DOES changed; what it is made of did. Before, the whole
 * step was hand-rolled and carried no shadow token at all, so not one surface
 * on it read as an object resting on the page:
 *
 *  - The step's title was a bare `<Subtitle2>` — a `<span>`, so a screen reader
 *    got a step with no heading. It is a `Section` now: a real `<h2>`, with
 *    `surface={false}` because its own children are the cards.
 *  - The dropzone is `useSurfaceStyles().card` (E1) with its border swapped for
 *    the dashed edge that IS the drop affordance — 8px, not the 4px it used to
 *    draw, and elevated like every other resting surface in the app.
 *  - Every file row, thumbnail tile and the undo toast are
 *    `useSurfaceStyles().inset` (E0): background3 and the 4px control radius,
 *    because a panel nested inside a card at the card's own 8px radius reads
 *    wrong. Three separately-invented boxes (`8px 12px`/background2,
 *    `8px 12px`/background3, and a bare grid cell) become one.
 *  - The type chip was a hand-built box carrying `padding: '0 6px'` — off the
 *    4px grid — plus a raw `fontSize` and `fontWeight`. It is a Fluent `Badge`,
 *    the same one `ReviewSummary` already uses for the same file type, so the
 *    two screens cannot drift.
 *  - Type comes from presets: the file name is `Body1Strong` (the row's
 *    identity), its size/progress/reason lines are `Caption1`. `fontSize:` and
 *    `fontWeight:` are banned declarations — `Body1` + semibold IS
 *    `Body1Strong`.
 *  - `thumbnailButton` is a native `<button>`, which does NOT inherit `color`
 *    from `FluentProvider`; it fell back to the browser's `buttontext` and only
 *    survived because its one text child set a colour of its own. It sets
 *    `colorNeutralForeground1` explicitly now (CLAUDE.md log entry 54).
 *
 * Filenames were already middle-ellipsised through `truncateFileNameMiddle`
 * (wireframe X2: `Quotation_Staples…Jul26.pdf`), so that rule needed no change.
 */

interface UploadingItem {
  kind: 'uploading';
  key: string;
  file: File;
  progress: number;
  cancel: () => void;
}
interface RejectedItem {
  kind: 'rejected';
  key: string;
  file: File;
  reason: string;
}
interface FailedItem {
  kind: 'failed';
  key: string;
  file: File;
  error: string;
}
interface UploadedItem {
  kind: 'uploaded';
  attachment: Attachment;
  /** Only set for files uploaded this session — lets the thumbnail render instantly, no round trip. */
  localFile?: File;
}
type AttachmentItem = UploadingItem | RejectedItem | FailedItem | UploadedItem;

function itemKey(item: AttachmentItem): string {
  return item.kind === 'uploaded' ? item.attachment.id : item.key;
}

function isImage(contentType: string): boolean {
  return contentType.startsWith('image/');
}

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${String(Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function typeLabel(contentType: string, fileName: string): string {
  if (contentType === 'application/pdf') return 'PDF';
  if (contentType.includes('word')) return 'DOC';
  if (contentType.includes('excel') || contentType.includes('spreadsheet')) return 'XLS';
  const extension = fileName.split('.').pop();
  return extension ? extension.toUpperCase() : 'FILE';
}

/** Wireframe 10.1 (D3) — 10 seconds, or until the next destructive action. */
const REMOVE_UNDO_MS = 10_000;

/** The shipped defaults, used until the live values (#258) resolve or if they never do. */
const DEFAULT_ATTACHMENT_LIMITS: AttachmentLimits = {
  maxAttachmentSizeBytes: MAX_ATTACHMENT_SIZE_BYTES,
  maxAttachmentsPerRequest: MAX_ATTACHMENTS_PER_REQUEST,
};

/**
 * Client-side echo of the API's own checks (wireframe note 3: "a courtesy, not a control").
 *
 * `limits` is the live, admin-configurable value fetched from
 * `GET /api/attachments/limits` (#258) — falling back to
 * `DEFAULT_ATTACHMENT_LIMITS` before that resolves or if it fails — so this
 * check cannot drift from what the backend actually enforces.
 */
function validateFile(
  file: File,
  currentCount: number,
  limits: AttachmentLimits,
): string | undefined {
  if (currentCount >= limits.maxAttachmentsPerRequest) {
    return `Maximum ${String(limits.maxAttachmentsPerRequest)} attachments per request`;
  }
  if (!ALLOWED_ATTACHMENT_TYPES.has(file.type)) {
    return 'Not accepted — allowed types are PDF, Word, Excel and images';
  }
  if (file.size > limits.maxAttachmentSizeBytes) {
    return `Too large — ${formatBytes(file.size)}, limit is ${formatBytes(limits.maxAttachmentSizeBytes)}`;
  }
  return undefined;
}

/** Resolves an object URL for an image — instantly from the local File when this session uploaded it, otherwise fetched once through the authenticated download route. */
function useAttachmentPreviewUrl(attachment: Attachment, localFile?: File): string | undefined {
  const [url, setUrl] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (!isImage(attachment.contentType)) return undefined;
    let objectUrl: string | undefined;
    let cancelled = false;
    if (localFile) {
      objectUrl = URL.createObjectURL(localFile);
      setUrl(objectUrl);
    } else {
      setUrl(undefined);
      void fetchAttachmentBlob(attachment.id)
        .then((blob) => {
          if (cancelled) return undefined;
          objectUrl = URL.createObjectURL(blob);
          setUrl(objectUrl);
          return undefined;
        })
        .catch(() => undefined);
    }
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment.id, attachment.contentType, localFile]);
  return url;
}

const useStyles = makeStyles({
  /**
   * Composed ON TOP of `surfaces.card`, so the dropzone is a resting surface
   * like every other block in the app — it used to have no shadow and the 4px
   * control radius, which read as a form field rather than a place to drop
   * something.
   *
   * The one thing it overrides is the card's own edge: a dashed border IS the
   * drop affordance, and `strokeWidthThick` here is a border WIDTH doing the
   * work rather than the brand hue, so it survives high contrast.
   *
   * Written as the whole `border` shorthand, exactly as `surfaces.card` writes
   * its own, and that is load-bearing: Griffel emits a shorthand as ONE atomic
   * declaration (the same behaviour `surfaces.tableCard` documents for
   * `overflow`) rather than expanding it into longhands. Two classes writing
   * the same key means `mergeClasses` picks a winner; a longhand override here
   * would leave two half-applied edges instead.
   */
  dropzone: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: SPACE.xs,
    border: `${tokens.strokeWidthThick} dashed ${tokens.colorNeutralStroke1}`,
    textAlign: 'center',
  },

  /**
   * Drag-over. The brand tint is NOT carrying this on its own — high contrast
   * discards the hue entirely — the copy itself changes to "Drop to upload",
   * which is what survives.
   */
  dropzoneActive: {
    border: `${tokens.strokeWidthThick} dashed ${tokens.colorBrandStroke1}`,
    backgroundColor: tokens.colorBrandBackground2,
  },

  /** De-emphasis is a foreground token, never `opacity`. */
  hint: { color: tokens.colorNeutralForeground2 },

  /**
   * Composed on top of `surfaces.inset` (E0). #53 — below the breakpoint the
   * actions wrap onto their own line instead of squeezing the file name off the
   * canvas. The row keeps its order: type chip, name, then actions.
   */
  fileRow: {
    display: 'flex',
    alignItems: 'center',
    gap: SPACE_X.m,
    '@media (max-width: 700px)': { flexWrap: 'wrap' },
  },

  /**
   * A rejected file keeps its tint, but the tint is never the only signal: the
   * reason is spelled out in the row and a Dismiss button sits beside it, both
   * of which survive a theme that collapses the palette.
   */
  rejectedRow: { backgroundColor: tokens.colorPaletteRedBackground1 },
  rejectedReason: { color: tokens.colorPaletteRedForeground1 },

  /** The chip must not be squeezed by a long file name beside it. */
  chip: { flexShrink: 0 },

  fileMeta: { display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0 },

  /**
   * The LAST-RESORT clip only. The middle ellipsis wireframe X2 actually asks
   * for — so `Quotation_Staples…Jul26.pdf` keeps its extension — is applied in
   * JS by `truncateFileNameMiddle` before the string ever reaches the DOM,
   * because CSS has no middle-truncation mode; this rule just stops an
   * already-shortened name from overflowing a very narrow row, and the full
   * name stays reachable through `title` either way.
   *
   * `block` on the element is required for `text-overflow` to apply at all: a
   * Fluent typography preset renders an inline `<span>`.
   */
  fileName: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },

  actions: { display: 'flex', gap: SPACE_X.xs, flexShrink: 0 },

  thumbnailGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
    gap: SPACE.m,
  },

  /** Composed on top of `surfaces.inset` — the image tile is the file row's
   *  counterpart, so the two read as one family rather than two inventions. */
  thumbnailTile: { display: 'flex', flexDirection: 'column', gap: SPACE.xs },

  /**
   * A native `<button>` does not inherit `color` from `FluentProvider` — it
   * falls back to the browser's `buttontext` system colour, which happened to
   * stay legible in light and dark and collided with pure black in high
   * contrast (CLAUDE.md log entry 54). The token is explicit for that reason,
   * not for decoration.
   */
  thumbnailButton: {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACE.xs,
    padding: SPACE.none,
    border: 'none',
    backgroundColor: 'transparent',
    color: tokens.colorNeutralForeground1,
    cursor: 'pointer',
    textAlign: 'left',
  },

  /** `colorNeutralBackground1` rather than 3: the tile around it is already
   *  background3, so the loading square would otherwise be invisible. */
  thumbnailImage: {
    width: '100%',
    aspectRatio: '1',
    objectFit: 'cover',
    borderRadius: RADIUS.control,
    backgroundColor: tokens.colorNeutralBackground1,
  },

  thumbnailCaption: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: tokens.colorNeutralForeground2,
  },

  lightboxImage: {
    maxWidth: '100%',
    maxHeight: '70vh',
    display: 'block',
    margin: '0 auto',
    borderRadius: RADIUS.control,
  },

  /**
   * Wireframe 10.1 (D3) — `surfaces.inset`, the same E0 panel the file rows
   * use, so the thing that replaces a row looks like the row it replaced.
   *
   * `<output>` is a native form element, so it needs BOTH a block box (it is
   * inline by default) and an explicit foreground token — neither is inherited
   * from `FluentProvider` (CLAUDE.md log entry 54).
   */
  removalToast: {
    display: 'flex',
    alignItems: 'center',
    gap: SPACE_X.s,
    color: tokens.colorNeutralForeground1,
  },
});

function ImageThumbnail({
  attachment,
  localFile,
  onOpen,
  onRemove,
}: {
  attachment: Attachment;
  localFile?: File;
  onOpen: () => void;
  onRemove: () => void;
}) {
  const styles = useStyles();
  const surfaces = useSurfaceStyles();
  const url = useAttachmentPreviewUrl(attachment, localFile);
  return (
    <div className={mergeClasses(surfaces.inset, styles.thumbnailTile)}>
      <button
        type="button"
        className={styles.thumbnailButton}
        onClick={onOpen}
        aria-label={`Open ${attachment.fileName}`}
      >
        {url ? (
          <Image src={url} alt="" className={styles.thumbnailImage} />
        ) : (
          <div className={styles.thumbnailImage}>
            <Spinner size="tiny" />
          </div>
        )}
        <Caption1 block className={styles.thumbnailCaption} title={attachment.fileName}>
          {truncateFileNameMiddle(attachment.fileName)}
        </Caption1>
      </button>
      <Button appearance="transparent" size="small" onClick={onRemove}>
        Remove
      </Button>
    </div>
  );
}

function onDragOver(event: ReactDragEvent<HTMLDivElement>) {
  event.preventDefault();
}

export interface AttachmentsSectionProps {
  /** Resolves to the request id, saving the draft first if it hasn't been saved yet. */
  ensureDraftId: () => Promise<string | undefined>;
  /** Reports the current set of successfully uploaded files — the review screen (#35) needs this list too. */
  onAttachmentsChange?: (attachments: Attachment[]) => void;
  /**
   * #260 — reports the in-memory `File` behind each uploaded-this-session
   * attachment, keyed by attachment id, alongside `onAttachmentsChange`.
   * Without this, `RequestForm.tsx` only ever sees the plain `Attachment[]`
   * and `ReviewSummary` one step later has no choice but to re-fetch an
   * image over the network — the root cause of #260's "blank image on
   * Review & Submit" bug.
   */
  onLocalFilesChange?: (files: Map<string, File>) => void;
  /** Files already on the request when this form opened (#42 — resuming a rejected request). */
  initialAttachments?: Attachment[];
}

export function AttachmentsSection({
  ensureDraftId,
  onAttachmentsChange,
  onLocalFilesChange,
  initialAttachments,
}: AttachmentsSectionProps) {
  const styles = useStyles();
  const surfaces = useSurfaceStyles();
  const layout = useLayoutStyles();
  const responsive = useResponsiveStyles();
  const [items, setItems] = useState<AttachmentItem[]>(() =>
    (initialAttachments ?? []).map((attachment) => ({ kind: 'uploaded', attachment })),
  );
  // Live upload limits (#258) — fetched once on mount so `validateFile` and
  // the dropzone hint text track an admin-changed value immediately instead
  // of the compiled-in defaults. Never blocks the UI: it starts at (and, on
  // failure, stays at) the same static defaults `attachmentLimits.ts` always
  // shipped, so there is nothing to wait on before the section is usable.
  const [limits, setLimits] = useState<AttachmentLimits>(DEFAULT_ATTACHMENT_LIMITS);
  useEffect(() => {
    let cancelled = false;
    void fetchAttachmentLimits()
      .then((fetched) => {
        if (!cancelled) setLimits(fetched);
        return undefined;
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const [dragActive, setDragActive] = useState(false);
  const dragDepth = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // #260 — one shared preview dialog for every attachment type; the index is
  // into `uploaded` (images AND documents), so Previous/Next in the dialog
  // moves across the whole set, not just the images.
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  // K1 "Dialog closes" — back to the thumbnail/row that opened the preview.
  useRestoreFocusOnClose(previewIndex !== null);

  // Wireframe D3 — removal shows an undo toast rather than deleting outright.
  // Only one removal is ever pending: starting a new one finalizes whatever
  // was already pending ("10 seconds, or until the next destructive action").
  const [pendingRemoval, setPendingRemovalState] = useState<UploadedItem | null>(null);
  const pendingRemovalRef = useRef<UploadedItem | null>(null);
  const removalTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const toastRef = useRef<HTMLOutputElement>(null);

  function setPendingRemoval(item: UploadedItem | null) {
    pendingRemovalRef.current = item;
    setPendingRemovalState(item);
  }

  function clearRemovalTimer() {
    if (removalTimerRef.current) {
      clearTimeout(removalTimerRef.current);
      removalTimerRef.current = null;
    }
  }

  // Called on timeout, on Esc, and when a later removal supersedes this one —
  // this is the one place the blob and row are actually, irreversibly deleted.
  function finalizePendingRemoval() {
    const current = pendingRemovalRef.current;
    clearRemovalTimer();
    setPendingRemoval(null);
    if (current) void deleteAttachment(current.attachment.id).catch(() => undefined);
  }

  function startRemoval(item: UploadedItem) {
    finalizePendingRemoval();
    setItems((prev) => prev.filter((existing) => itemKey(existing) !== itemKey(item)));
    setPendingRemoval(item);
    removalTimerRef.current = setTimeout(finalizePendingRemoval, REMOVE_UNDO_MS);
  }

  function undoRemoval() {
    const current = pendingRemovalRef.current;
    if (!current) return;
    clearRemovalTimer();
    setPendingRemoval(null);
    setItems((prev) => [...prev, current]);
  }

  useEffect(() => {
    if (!pendingRemoval) return undefined;
    toastRef.current?.focus();
    // Wireframe D3: "Esc dismisses without undoing" — a document listener
    // rather than an onKeyDown so it fires regardless of which element inside
    // the toast (or its Undo button) currently has focus.
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') finalizePendingRemoval();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [pendingRemoval]);

  useEffect(() => clearRemovalTimer, []);

  function replacePending(key: string, next: AttachmentItem) {
    setItems((prev) =>
      prev.map((item) => (item.kind !== 'uploaded' && item.key === key ? next : item)),
    );
  }

  function startUpload(file: File) {
    const key = crypto.randomUUID();
    setItems((prev) => [
      ...prev,
      { kind: 'uploading', key, file, progress: 0, cancel: () => undefined },
    ]);

    void (async () => {
      const requestId = await ensureDraftId();
      if (!requestId) {
        replacePending(key, {
          kind: 'failed',
          key,
          file,
          error: 'Could not save the draft — try again',
        });
        return;
      }

      const upload = uploadAttachment(requestId, file, (fraction) => {
        setItems((prev) =>
          prev.map((item) =>
            item.kind === 'uploading' && item.key === key
              ? { ...item, progress: Math.round(fraction * 100) }
              : item,
          ),
        );
      });
      setItems((prev) =>
        prev.map((item) =>
          item.kind === 'uploading' && item.key === key ? { ...item, cancel: upload.cancel } : item,
        ),
      );

      try {
        const attachment = await upload.result;
        replacePending(key, { kind: 'uploaded', attachment, localFile: file });
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          setItems((prev) => prev.filter((item) => itemKey(item) !== key));
          return;
        }
        const message =
          error instanceof Error ? error.message : 'Upload failed — check your connection';
        replacePending(key, { kind: 'failed', key, file, error: message });
      }
    })();
  }

  function handleFiles(files: Iterable<File>) {
    let count = items.filter(
      (item) => item.kind === 'uploaded' || item.kind === 'uploading',
    ).length;
    for (const file of files) {
      const reason = validateFile(file, count, limits);
      if (reason) {
        setItems((prev) => [...prev, { kind: 'rejected', key: crypto.randomUUID(), file, reason }]);
        continue;
      }
      count += 1;
      startUpload(file);
    }
  }

  function retry(item: FailedItem) {
    setItems((prev) => prev.filter((existing) => itemKey(existing) !== item.key));
    startUpload(item.file);
  }

  function dismiss(key: string) {
    setItems((prev) => prev.filter((item) => itemKey(item) !== key));
  }

  function onDragEnter(event: ReactDragEvent<HTMLDivElement>) {
    event.preventDefault();
    dragDepth.current += 1;
    setDragActive(true);
  }
  function onDragLeave(event: ReactDragEvent<HTMLDivElement>) {
    event.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragActive(false);
  }
  function onDrop(event: ReactDragEvent<HTMLDivElement>) {
    event.preventDefault();
    dragDepth.current = 0;
    setDragActive(false);
    handleFiles(event.dataTransfer.files);
  }
  function onFileInputChange(event: ChangeEvent<HTMLInputElement>) {
    if (event.target.files) handleFiles(event.target.files);
    event.target.value = '';
  }

  const uploaded = items.filter((item): item is UploadedItem => item.kind === 'uploaded');
  const documents = uploaded.filter((item) => !isImage(item.attachment.contentType));
  const images = uploaded.filter((item) => isImage(item.attachment.contentType));
  const pending = items.filter(
    (item): item is UploadingItem | RejectedItem | FailedItem => item.kind !== 'uploaded',
  );
  const totalBytes = uploaded.reduce((sum, item) => sum + item.attachment.sizeBytes, 0);
  const uploadedIds = uploaded.map((item) => item.attachment.id).join(',');

  useEffect(() => {
    onAttachmentsChange?.(uploaded.map((item) => item.attachment));
    onLocalFilesChange?.(
      new Map(
        uploaded
          .filter((item): item is UploadedItem & { localFile: File } => !!item.localFile)
          .map((item) => [item.attachment.id, item.localFile]),
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fires only when the uploaded id set actually changes, not on every render
  }, [uploadedIds]);

  return (
    // `touchTargets` sits outside the `Section` so it also reaches anything the
    // heading row carries, the same placement `ReviewSummary` uses.
    <div className={responsive.touchTargets}>
      {/* No table here — the file list is already one stacked row per file.
          `surface={false}`: this step's own children are the cards, and an 8px
          card inside an 8px card reads wrong. */}
      <Section
        level={2}
        surface={false}
        title="Attachments"
        description={
          uploaded.length > 0 ? (
            <>
              {uploaded.length} {uploaded.length === 1 ? 'file' : 'files'} ·{' '}
              {formatBytes(totalBytes)}
            </>
          ) : undefined
        }
      >
        {/* One stack owns the rhythm between the dropzone, the rows and the
            thumbnail grid; no block sets a margin of its own. */}
        <div className={layout.stack}>
          <div
            className={mergeClasses(
              surfaces.card,
              styles.dropzone,
              dragActive ? styles.dropzoneActive : undefined,
            )}
            onDragEnter={onDragEnter}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
          >
            <Body1 as="p">
              {dragActive ? 'Drop to upload' : 'Drag files here, or'}{' '}
              {!dragActive && (
                <Button
                  appearance="transparent"
                  size="small"
                  onClick={() => fileInputRef.current?.click()}
                >
                  browse
                </Button>
              )}
            </Body1>
            <Caption1 block className={styles.hint}>
              PDF, Word, Excel or images · up to {formatBytes(limits.maxAttachmentSizeBytes)} each
            </Caption1>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              onChange={onFileInputChange}
              aria-label="Browse for files to attach"
            />
          </div>

          {pending.map((item) => (
            <div
              key={item.key}
              className={mergeClasses(
                surfaces.inset,
                styles.fileRow,
                item.kind === 'rejected' ? styles.rejectedRow : undefined,
              )}
            >
              <Badge appearance="tint" color="informative" className={styles.chip}>
                {typeLabel(item.file.type, item.file.name)}
              </Badge>
              <div className={styles.fileMeta}>
                <Body1Strong block className={styles.fileName} title={item.file.name}>
                  {truncateFileNameMiddle(item.file.name)}
                </Body1Strong>
                {item.kind === 'uploading' && (
                  <>
                    <ProgressBar value={item.progress / 100} />
                    <Caption1 className={styles.hint}>
                      {item.progress}% · {formatBytes((item.progress / 100) * item.file.size)} of{' '}
                      {formatBytes(item.file.size)}
                    </Caption1>
                  </>
                )}
                {item.kind === 'rejected' && (
                  <Caption1 className={styles.rejectedReason}>{item.reason}</Caption1>
                )}
                {item.kind === 'failed' && (
                  <Caption1 className={styles.rejectedReason}>{item.error}</Caption1>
                )}
              </div>
              <div className={styles.actions}>
                {item.kind === 'uploading' && (
                  <Button appearance="transparent" size="small" onClick={item.cancel}>
                    Cancel
                  </Button>
                )}
                {item.kind === 'rejected' && (
                  <Button appearance="transparent" size="small" onClick={() => dismiss(item.key)}>
                    Dismiss
                  </Button>
                )}
                {item.kind === 'failed' && (
                  <Button appearance="transparent" size="small" onClick={() => retry(item)}>
                    Retry
                  </Button>
                )}
              </div>
            </div>
          ))}

          {documents.map((item) => (
            <div key={item.attachment.id} className={mergeClasses(surfaces.inset, styles.fileRow)}>
              <Badge appearance="tint" color="informative" className={styles.chip}>
                {typeLabel(item.attachment.contentType, item.attachment.fileName)}
              </Badge>
              <div className={styles.fileMeta}>
                <Body1Strong block className={styles.fileName} title={item.attachment.fileName}>
                  {truncateFileNameMiddle(item.attachment.fileName)}
                </Body1Strong>
                <Caption1 className={styles.hint}>
                  {formatBytes(item.attachment.sizeBytes)}
                </Caption1>
              </div>
              <div className={styles.actions}>
                <Button
                  appearance="secondary"
                  size="small"
                  onClick={() =>
                    setPreviewIndex(
                      uploaded.findIndex((u) => u.attachment.id === item.attachment.id),
                    )
                  }
                >
                  Preview
                </Button>
                <Button appearance="secondary" size="small" onClick={() => startRemoval(item)}>
                  Remove
                </Button>
              </div>
            </div>
          ))}

          {images.length > 0 && (
            <div className={styles.thumbnailGrid}>
              {images.map((item) => (
                <ImageThumbnail
                  key={item.attachment.id}
                  attachment={item.attachment}
                  localFile={item.localFile}
                  onOpen={() =>
                    setPreviewIndex(
                      uploaded.findIndex((u) => u.attachment.id === item.attachment.id),
                    )
                  }
                  onRemove={() => startRemoval(item)}
                />
              ))}
            </div>
          )}

          {previewIndex !== null && uploaded[previewIndex] && (
            <AttachmentPreviewDialog
              attachments={uploaded.map((item) => item.attachment)}
              initialIndex={previewIndex}
              localFiles={
                new Map(
                  uploaded
                    .filter((item): item is UploadedItem & { localFile: File } => !!item.localFile)
                    .map((item) => [item.attachment.id, item.localFile]),
                )
              }
              onClose={() => setPreviewIndex(null)}
            />
          )}

          {pendingRemoval && (
            <output
              ref={toastRef}
              aria-label="Undo attachment removal"
              tabIndex={-1}
              className={mergeClasses(surfaces.inset, styles.removalToast)}
            >
              <Body1>{pendingRemoval.attachment.fileName} removed</Body1>
              <Button appearance="transparent" size="small" onClick={undoRemoval}>
                Undo
              </Button>
            </output>
          )}
        </div>
      </Section>
    </div>
  );
}
